import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Skeleton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  SportsEsports as SportsEsportsIcon,
  ToggleOn,
  ToggleOff,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../../api';
import { toast } from 'react-toastify';

const GameContentList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [gameTypeFilter, setGameTypeFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [gameTypes, setGameTypes] = useState([]);
  const [subjects, setSubjects] = useState([]);

  useEffect(() => {
    api.get('admin/game-contents/game-types').then((r) => {
      if (r.data?.success && r.data?.data) setGameTypes(r.data.data);
    }).catch(() => {});
    api.get('admin/game-contents/subjects').then((r) => {
      if (r.data?.success && r.data?.data) setSubjects(r.data.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchList();
  }, [page, rowsPerPage, gameTypeFilter, subjectFilter, statusFilter]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, per_page: rowsPerPage };
      if (gameTypeFilter) params.game_type = gameTypeFilter;
      if (subjectFilter) params.subject_id = subjectFilter;
      if (statusFilter !== '') params.status = statusFilter;
      const res = await api.get('admin/game-contents', { params });
      if (res.data?.success && res.data?.data) {
        const pag = res.data.data;
        setItems(Array.isArray(pag.data) ? pag.data : []);
        setTotalRecords(pag.total ?? 0);
      } else {
        setItems([]);
        setTotalRecords(0);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load game contents');
      setItems([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (row) => {
    setSelectedItem(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) return;
    try {
      await api.delete(`admin/game-contents/${selectedItem.id}`);
      toast.success('Game content deleted');
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete');
    }
  };

  const handleStatusToggle = async (row) => {
    const newStatus = row.status === 2 ? 1 : 2;
    try {
      await api.patch(`admin/game-contents/${row.id}/status`, { status: newStatus });
      toast.success('Status updated');
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update status');
    }
  };

  const getStatusLabel = (status) => (status === 2 ? 'Active' : 'Inactive');
  const getStatusColor = (status) => (status === 2 ? 'success' : 'default');

  const contentPreview = (content) => {
    if (!content) return '—';
    if (typeof content === 'string') return content.slice(0, 50);
    const q = content.question || content.title || '';
    if (q) return String(q).slice(0, 50) + (String(q).length > 50 ? '…' : '');
    return JSON.stringify(content).slice(0, 50) + '…';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          background: 'linear-gradient(135deg, #5e35b1 0%, #7e57c2 100%)',
          borderRadius: '25px',
          px: 3,
          py: 1,
          boxShadow: '0 4px 15px rgba(94, 53, 177, 0.3)',
        }}>
          <SportsEsportsIcon sx={{ fontSize: '24px', color: 'white' }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
            Game Contents
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 1 }}>
          <Typography variant="body1" sx={{ color: '#5a6c7d' }}>
            Manage questions and content items for each game type.
          </Typography>
          {!loading && (
            <Chip label={`Total: ${totalRecords}`} size="small" sx={{ bgcolor: '#5e35b1', color: 'white' }} />
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Game type</InputLabel>
          <Select
            value={gameTypeFilter}
            onChange={(e) => { setGameTypeFilter(e.target.value); setPage(0); }}
            label="Game type"
          >
            <MenuItem value="">All</MenuItem>
            {gameTypes.map((key) => (
              <MenuItem key={key} value={key}>{key}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Subject</InputLabel>
          <Select
            value={subjectFilter}
            onChange={(e) => { setSubjectFilter(e.target.value); setPage(0); }}
            label="Subject"
          >
            <MenuItem value="">All</MenuItem>
            {subjects.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value={1}>Inactive</MenuItem>
            <MenuItem value={2}>Active</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/game-contents/add')}
          sx={{ bgcolor: '#5e35b1', '&:hover': { bgcolor: '#4527a0' } }}
        >
          Add Game Content
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, width: '12%' }}>Game type</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '12%' }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '14%' }}>Topic</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '14%' }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '20%' }}>Content preview</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '8%' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Difficulty</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell><Skeleton variant="text" width={100} /></TableCell>
                    <TableCell><Skeleton variant="text" width={120} /></TableCell>
                    <TableCell><Skeleton variant="text" width={150} /></TableCell>
                    <TableCell><Skeleton variant="text" width={60} /></TableCell>
                    <TableCell><Skeleton variant="text" width={50} /></TableCell>
                    <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <SportsEsportsIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                    <Typography color="text.secondary">No game contents found</Typography>
                    <Button startIcon={<AddIcon />} onClick={() => navigate('/admin/game-contents/add')} sx={{ mt: 2 }}>
                      Add Game Content
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell><Chip label={row.game_type} size="small" variant="outlined" /></TableCell>
                    <TableCell>{row.subject_name || '—'}</TableCell>
                    <TableCell>{row.topic_name || '—'}</TableCell>
                    <TableCell>{row.title || '—'}</TableCell>
                    <TableCell
                      sx={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                      }}
                    >
                      {contentPreview(row.content)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(row.status)}
                        color={getStatusColor(row.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{row.difficulty || '—'}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleStatusToggle(row)}
                        title={row.status === 2 ? 'Set Inactive' : 'Set Active'}
                      >
                        {row.status === 2 ? <ToggleOn color="primary" /> : <ToggleOff />}
                      </IconButton>
                      <IconButton size="small" title="View" onClick={() => navigate(`/admin/game-contents/${row.id}`)}>
                        <ViewIcon />
                      </IconButton>
                      <IconButton size="small" color="primary" onClick={() => navigate(`/admin/game-contents/${row.id}/edit`)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(row)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && items.length > 0 && (
          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Paper>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Game Content</DialogTitle>
        <DialogContent>
          <Typography>
            Delete this game content? (Game type: {selectedItem?.game_type}, Title: {selectedItem?.title || '—'})
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GameContentList;
